import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from './config/api';

/**
 * Μόνιμη προειδοποίηση όταν το ταμείο έμεινε ΑΝΟΙΧΤΟ από προηγούμενη μέρα
 * (κατάστημα με χειροκίνητο κλείσιμο που ξέχασε το χθεσινό Κλείσιμο Ταμείου — μενού → «Ταμείο»).
 * Χωρίς αυτήν, το λάθος φαινόταν μόνο στο επόμενο κλείσιμο — με «διπλό ταμείο»
 * που χρειάζεται διόρθωση από την υποστήριξη. Ελέγχει στο mount και κάθε 10'.
 *
 * Η πλήρης μπάρα κάθεται ΠΑΝΩ από το navigation και μπλόκαρε τα κουμπιά του —
 * γι' αυτό έχει «Απόκρυψη»: μαζεύεται σε μικρό αναδιπλωμένο badge κάτω
 * αριστερά ώστε ο ταμίας να δουλεύει, και ξαναεμφανίζεται ανά ώρα μέχρι να
 * γίνει το κλείσιμο (το πρόβλημα δεν πρέπει να ξεχαστεί τελείως).
 */
const DISMISS_KEY = 'stale_register_dismissed_at';
const REDISPLAY_MS = 60 * 60 * 1000; // re-nag κάθε 1 ώρα

const StaleRegisterBanner: React.FC = () => {
  const { store } = useAuth();
  const [status, setStatus] = useState<{ is_stale: boolean; period_start: string; hours_open: number } | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const t = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return Date.now() - t < REDISPLAY_MS;
  });

  const check = useCallback(async () => {
    if (!store?.id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/orders/${store.id}/register-status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` },
      });
      if (res.ok) setStatus(await res.json());
    } catch { /* best-effort */ }
    // Έληξε η ώρα «χάριτος» της απόκρυψης; Ξαναδείξε την πλήρη μπάρα.
    const t = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (Date.now() - t >= REDISPLAY_MS) setCollapsed(false);
  }, [store?.id]);

  useEffect(() => {
    check();
    const t = setInterval(check, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, [check]);

  if (!status?.is_stale) return null;

  const since = new Date(status.period_start).toLocaleString('el-GR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });

  if (collapsed) {
    return (
      <button
        onClick={() => { localStorage.removeItem(DISMISS_KEY); setCollapsed(false); }}
        className="fixed bottom-3 left-3 z-[96] flex items-center gap-2 bg-amber-600 text-slate-950 pl-2.5 pr-3 py-2 rounded-full text-xs font-semibold shadow-lg hover:bg-amber-500"
        title={`Το ταμείο είναι ανοιχτό από ${since} — δεν έγινε το χθεσινό «Κλείσιμο ταμείου» (μενού → Ταμείο). Πάτησε για λεπτομέρειες.`}
      >
        <AlertTriangle className="w-4 h-4 shrink-0" />
        Ανοιχτό ταμείο από {since}
      </button>
    );
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[96] bg-amber-600 text-slate-950 px-4 py-2 flex items-center justify-center gap-2 text-sm font-semibold shadow-lg">
      <AlertTriangle className="w-4 h-4 shrink-0" />
      <span>
        Το ταμείο είναι ανοιχτό από {since} ({status.hours_open}ω) — δεν έγινε το χθεσινό «Κλείσιμο ταμείου» (μενού → Ταμείο).
        Ενημέρωσε τον υπεύθυνο· αν χρειάζεται διαχωρισμός ημερών, κάλεσε την υποστήριξη (210 700 5831) πριν το σημερινό κλείσιμο.
      </span>
      <button
        onClick={() => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setCollapsed(true); }}
        className="ml-2 shrink-0 flex items-center gap-1 bg-slate-950/15 hover:bg-slate-950/25 rounded-lg px-2.5 py-1 text-xs"
        title="Απόκρυψη για 1 ώρα — θα μείνει μικρή ένδειξη κάτω αριστερά"
      >
        <X className="w-3.5 h-3.5" />
        Απόκρυψη
      </button>
    </div>
  );
};

export default StaleRegisterBanner;
